/**
 * WHO IS CALLING — the one wire vocabulary, carried by one header.
 *
 * This answers "which piece of Bower software made this request", and it is
 * the general fact several features need:
 *
 *  - `Artifact.captureSurface` DERIVES from it (`captureSurfaceForClient`),
 *    since Phase 2 shipped on 2026-09-17. `resolveCaptureSurface` in the
 *    backend reads the ambient scope this header opens. It was the only
 *    server-side consumer until the audit column below.
 *  - `audit_logs.bower_client` stores it DIRECTLY. Phase 4 of
 *    `docs/planning/audit-log-chain-of-custody.md` added the column and wired
 *    all seventeen writers to it; that code landed on 2026-09-18. An audit row
 *    answers "which piece of Bower software did this", which it could not
 *    before — it recorded `ipAddress` and `userAgent` and nothing naming the
 *    client. Every writer resolves the value through ONE function,
 *    `backend/src/utils/auditBowerClient.ts`, and the row stores `unknown`
 *    when the request declared nothing.
 *
 *    A row carries a value only from the deploy that includes that code
 *    onward. Every row written before it is NULL, and NULL is not
 *    backfillable: the fact was never recorded. Read NULL as "not recorded",
 *    never as a client and never as `unknown`.
 *  - `User Signed In` records it raw as `bower_client`; `Onboarding Completed`
 *    maps it to `platform` (`onboardingPlatformProps`, both in the backend's
 *    auth controller).
 *  - `signupAttestation` and `mobileClientBuild` in the backend read the raw
 *    header directly: the native signup branch and the access log's build
 *    fields.
 *  - Support triage and per-client rate limiting COULD read it without another
 *    header being invented. Neither does.
 *
 * So those consumers read this header in code and the rest are intent — worth
 * stating plainly rather than in the future tense. The gain over the
 * predecessor header is that the fact is general and un-forgeable rather than
 * a conclusion the client asserts, and that it is recorded where an auditor
 * reads it rather than only derived into another column.
 *
 * It replaced `AUDIT_SOURCES`, a second vocabulary for the same question that
 * had no runtime consumer, deleted alongside this module in Task 1.2. It also
 * replaces `x-bower-capture-surface`, which named one consumer of this fact
 * and so could not be reused. That header is HALF retired: Phase 6a stopped
 * the server reading it, and it stays in the CORS `allowedHeaders` until
 * Phase 6b, so a tab that predates Phase 4 does not lose every request.
 *
 * There is deliberately no `unknown` member. A request that declares nothing
 * is absent from this vocabulary, not a member of it; storage layers that
 * need a literal write their own `unknown` when the scope is empty — which
 * the audit column now does, as `AUDIT_BOWER_CLIENTS` in `./audit.ts`
 * (every member here plus that one literal). Deliberately not a COUNT: this
 * list is append-only by its own instruction, so a number here is a sentence
 * that falsifies itself on the next append. Putting `unknown` on the wire
 * would invite a client to send it.
 *
 * NO RAW OBJECT INDEXING IN THIS SUBSYSTEM
 * ----------------------------------------
 * Every lookup keyed by a value that can originate on the wire is a `Map` or
 * a `Set`, never an object literal indexed with `obj[key]`. An object literal
 * carries `Object.prototype`, so `__proto__`, `constructor`, `toString`,
 * `valueOf`, `hasOwnProperty`, `isPrototypeOf`, `propertyIsEnumerable` and
 * `toLocaleString` all return truthy non-members that pass a `if (value)`
 * guard and defeat an `=== undefined` check. This bit the backend's legacy
 * header map, where the eight keys above turned an unauthenticated request
 * into a 500 at every artifact-creation door. Keep the rule at the subsystem
 * level: patching one instance leaves the next one to be written.
 */
import { CAPTURE_SURFACE_AGENT_CHAT, CAPTURE_SURFACE_API, CAPTURE_SURFACE_CONNECTOR, CAPTURE_SURFACE_DESKTOP, CAPTURE_SURFACE_LIVE_SESSION, CAPTURE_SURFACE_MOBILE, } from './capture';
/** The one client-identity header. Value must be a `BowerClient`. */
export const BOWER_CLIENT_HEADER = 'x-bower-client';
/**
 * The native app's build, sent by `mobile-app` only (bower-mobile-app #136):
 * `<semver>[+<build>]`, present only when the version is semver.
 */
export const BOWER_CLIENT_VERSION_HEADER = 'x-bower-client-version';
/** The native app's OTA update: an EAS update id, or `embedded` for the store binary. */
export const BOWER_CLIENT_UPDATE_HEADER = 'x-bower-client-update';
/**
 * Append, never reorder or rename: `audit_logs.bower_client` persists these
 * literals verbatim (audit plan Phase 4), so from the deploy that ships that
 * column onward a rename orphans every row already written. Same contract as
 * `AUDIT_ACTIONS` in `./audit.ts`.
 */
export const BOWER_CLIENTS = [
    /** The React app, desktop-width viewport. */
    'web',
    /**
     * The React app, mobile-width viewport. A separate member rather than a
     * second header: form factor is a property of the web client alone, and
     * every other client's form factor is fixed by its build.
     */
    'web-mobile',
    /** The native Expo app (`bower-labs/bower-mobile-app`). */
    'mobile-app',
    /** A glasses companion talking to the backend directly. */
    'glasses',
    /** The Python agent service, acting for a user. */
    'agent',
    /** An external MCP client (Claude Desktop and friends). */
    'mcp',
    /** The federated "Save to Bower" connector. */
    'connector',
    /** A backend background task or Cloud Task handler. */
    'job',
    /** An inbound provider webhook (Stripe). */
    'webhook',
    /**
     * The Python agent service, acting for a user INSIDE a live voice/video
     * session. Appended 2026-09-21 (the list is append-only — `bower_client`
     * persists these literals verbatim).
     *
     * It exists because `agent` maps to `agent_chat`, and Bird calls the same
     * MCP tools from both surfaces. A note Bird wrote during a live session
     * therefore landed on the chat surface and was absent from the live-session
     * Capture Stream, while every artifact the live-specific tools wrote
     * (`capture_photo`, the recorder, the voice note) carried `live_session`.
     * Measured in production session c7d47013 (2026-09-20): five artifacts, four
     * `live_session`, and the one Bird created through `create_artifact` on
     * `agent_chat` — the one the user went looking for and could not find.
     *
     * Server-assigned, not client-declarable: the MCP dispatcher derives it from
     * the already-clamped `x-bower-surface` analytics dimension. That header IS
     * caller-supplied, so a Firebase-authenticated browser could claim it — the
     * same trade `agent` already makes, and its labelling cost is the same one: a
     * user mislabels their own rows in their own workspace. The value is also half
     * of the `live-cell-edit` capture gate; the other half is the agent's verified
     * service identity, because this header alone is caller-asserted
     * (`mcp/live-cell-edit.ts`).
     */
    'agent-live',
];
/**
 * The clients that may name themselves on the wire.
 *
 * `agent` is here even though a browser could claim it, because the agent
 * service authenticates with the ACTING USER'S OWN Firebase token and the
 * backend cannot tell it apart from the web client. The honest cost is that
 * a user mislabels their own rows in their own workspace, which is the same
 * trade #2547 already made for the body-declarable surfaces.
 *
 * `agent-live`, `mcp`, `connector`, `job` and `webhook` are assigned
 * server-side, at the door that already knows: the MCP dispatcher (which
 * already computes `isExternalClient` and reads the clamped surface), the
 * connector import run, the task handler, the webhook route.
 */
const CLIENT_DECLARABLE_BOWER_CLIENTS = [
    'web',
    'web-mobile',
    'mobile-app',
    'glasses',
    'agent',
];
const CLIENT_DECLARABLE_SET = new Set(CLIENT_DECLARABLE_BOWER_CLIENTS);
export function isClientDeclarableBowerClient(value) {
    return CLIENT_DECLARABLE_SET.has(value);
}
/**
 * Client to capture surface, as an exhaustive `Record` so a new client cannot
 * be added without deciding what it captures as: the compiler asks. This is
 * the COMPILE-TIME half only — nothing indexes it at runtime. The `Map` below
 * is derived from it and is what lookups go through, because an object
 * literal indexed by a wire-derived key reaches `Object.prototype`; see the
 * no-raw-object-indexing note at the top of this file.
 *
 * `mcp` maps to `api`, not `agent_chat`. An external programmatic client is
 * an API consumer; only Bird's own in-app tools are an agent turn. Losing
 * "it came via MCP" from this column is the point, because the audit row is
 * where `mcp` gets recorded exactly — which `audit_logs.bower_client` does,
 * so each column keeps its own vocabulary instead of one being read as a
 * lossy proxy for the other. The two are NEVER interchangeable: a
 * `CAPTURE_SURFACES` value must never be written into the client column.
 */
const CAPTURE_SURFACE_BY_CLIENT_RECORD = {
    web: CAPTURE_SURFACE_DESKTOP,
    'web-mobile': CAPTURE_SURFACE_MOBILE,
    'mobile-app': CAPTURE_SURFACE_MOBILE,
    glasses: CAPTURE_SURFACE_LIVE_SESSION,
    agent: CAPTURE_SURFACE_AGENT_CHAT,
    // Same agent, different surface. `glasses` above already maps here, and for
    // the same reason: what the column answers is "where was the user when this
    // was born", and they were in a live session either way.
    'agent-live': CAPTURE_SURFACE_LIVE_SESSION,
    mcp: CAPTURE_SURFACE_API,
    connector: CAPTURE_SURFACE_CONNECTOR,
    job: CAPTURE_SURFACE_API,
    webhook: CAPTURE_SURFACE_API,
};
/**
 * The Record above is what the compiler checks for exhaustiveness; this Map is
 * what runtime indexes. Deriving one from the other keeps both properties:
 * a new `BowerClient` still fails to compile until it is mapped, and a lookup
 * key can never reach `Object.prototype`. See the no-raw-object-indexing note
 * at the top of this file.
 */
const CAPTURE_SURFACE_BY_CLIENT = new Map(Object.entries(CAPTURE_SURFACE_BY_CLIENT_RECORD));
export function captureSurfaceForClient(client) {
    const surface = CAPTURE_SURFACE_BY_CLIENT.get(client);
    // The signature promises a CaptureSurface unconditionally, and a bare index
    // cannot keep that promise: the backend sets `strict: false`, so an absent
    // header's `undefined` satisfies `BowerClient` and would return `undefined`
    // typed as a surface — a NULL row, silently, which is the exact failure this
    // header replaces. Throw instead of widening the return type: every caller
    // deciding its own fallback is the per-call-site default that produced the
    // 56.6%.
    if (surface === undefined) {
        throw new Error(`captureSurfaceForClient: not a BowerClient: ${String(client)}`);
    }
    return surface;
}
//# sourceMappingURL=client.js.map